import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { EmptyState } from "@/components/ui/empty-state";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

import playgroundApi from "../api/playgroundApi";
import type { PlaygroundMessage } from "../types";
import { formatRelativeTime, parseTimestamp } from "../utils/time";

type SearchMatchType = "title" | "message";

// Minimal conversation shape for sidebar search (we don’t need the full record here).
export type PlaygroundConversationRef = {
  playground_conversation_id: string;
  title?: string | null;
  last_message_at?: unknown;
};

type SearchResult = {
  conversation: PlaygroundConversationRef;
  matchType: SearchMatchType;
  subtitle: string;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversations: PlaygroundConversationRef[];
  selectedConversationId: string | null;
  onSelectConversation: (playgroundConversationId: string) => void;
};

function getConversationTitle(c: PlaygroundConversationRef): string {
  return (c.title ?? "").trim() || "Untitled conversation";
}

function getConversationSubtitle(c: PlaygroundConversationRef): string {
  const date = parseTimestamp(c.last_message_at);
  if (date) return formatRelativeTime(date);
  return "—";
}

function normalize(s: string): string {
  return s.trim().toLowerCase();
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function renderHighlightedText(text: string, query: string): React.ReactNode {
  const q = query.trim();
  if (!q) return text;
  const re = new RegExp(`(${escapeRegExp(q)})`, "ig");
  const parts = text.split(re);
  return parts.map((part, idx) => {
    if (idx % 2 === 1) return <strong key={idx}>{part}</strong>;
    return <React.Fragment key={idx}>{part}</React.Fragment>;
  });
}

function findSnippet(content: string, q: string): string | null {
  const hay = content ?? "";
  const hayLower = hay.toLowerCase();
  const idx = hayLower.indexOf(q);
  if (idx < 0) return null;
  const start = Math.max(0, idx - 40);
  const end = Math.min(hay.length, idx + q.length + 60);
  const prefix = start > 0 ? "…" : "";
  const suffix = end < hay.length ? "…" : "";
  return `${prefix}${hay.slice(start, end).trim()}${suffix}`;
}

function getMessageSearchHit(
  messages: PlaygroundMessage[],
  q: string
): string | null {
  for (const m of messages) {
    const content = (m.content ?? "").toString();
    const snippet = findSnippet(content, q);
    if (snippet) return snippet;
  }
  return null;
}

export function ConversationSearchDialog({
  open,
  onOpenChange,
  conversations,
  selectedConversationId,
  onSelectConversation
}: Props) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const messagesCacheRef = useRef<Map<string, PlaygroundMessage[]>>(new Map());
  const searchRunIdRef = useRef(0);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const normalizedQuery = useMemo(() => normalize(query), [query]);

  useEffect(() => {
    if (!open) return undefined;
    window.setTimeout(() => inputRef.current?.focus(), 0);
    return undefined;
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;

    const q = normalizedQuery;
    if (!q) {
      setIsSearching(false);
      setResults([]);
      return undefined;
    }

    searchRunIdRef.current += 1;
    const runId = searchRunIdRef.current;
    setIsSearching(true);

    const timer = window.setTimeout(() => {
      const doSearch = async () => {
        const titleMatches = conversations.filter((c) =>
          getConversationTitle(c).toLowerCase().includes(q)
        );

        // Show title matches immediately (so UI feels responsive).
        setResults(
          titleMatches.map((c) => ({
            conversation: c,
            matchType: "title",
            subtitle: "Title match"
          }))
        );

        const titleMatchIds = new Set(
          titleMatches.map((c) => c.playground_conversation_id)
        );

        const candidates = conversations.filter(
          (c) => !titleMatchIds.has(c.playground_conversation_id)
        );

        const processCandidate = async (
          current: PlaygroundConversationRef
        ): Promise<SearchResult | null> => {
          if (searchRunIdRef.current !== runId) return null;
          const convoId = current.playground_conversation_id;
          try {
            let msgs = messagesCacheRef.current.get(convoId);
            if (!msgs) {
              msgs = await playgroundApi.listMessages({
                playgroundConversationId: convoId,
                limit: 200
              });
              messagesCacheRef.current.set(convoId, msgs);
            }

            const hit = getMessageSearchHit(msgs, q);
            if (!hit) return null;
            return {
              conversation: current,
              matchType: "message",
              subtitle: hit
            };
          } catch {
            // Ignore fetch failures for search; user can still search titles.
            return null;
          }
        };

        const messageMatches: SearchResult[] = [];
        const batchSize = 6;
        const runBatches = (offset: number): Promise<void> => {
          if (searchRunIdRef.current !== runId) return Promise.resolve();
          const batch = candidates.slice(offset, offset + batchSize);
          if (batch.length === 0) return Promise.resolve();
          return Promise.all(batch.map(processCandidate)).then((hits) => {
            hits.forEach((h) => {
              if (h) messageMatches.push(h);
            });
            return runBatches(offset + batchSize);
          });
        };

        await runBatches(0);

        if (searchRunIdRef.current !== runId) return;

        setResults([
          ...titleMatches.map((c) => ({
            conversation: c,
            matchType: "title" as const,
            subtitle: "Title match"
          })),
          ...messageMatches
        ]);
        setIsSearching(false);
      };

      doSearch().catch(() => {
        if (searchRunIdRef.current === runId) setIsSearching(false);
      });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [conversations, normalizedQuery, open]);

  const recentResults: SearchResult[] = useMemo(() => {
    // Show recent conversations when query is empty.
    return conversations.slice(0, 10).map((c) => ({
      conversation: c,
      matchType: "title",
      subtitle: getConversationSubtitle(c)
    }));
  }, [conversations]);

  const displayResults = normalizedQuery ? results : recentResults;
  const showRightTimestamp = Boolean(normalizedQuery);

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (!next) {
          // Cancel any in-flight search UI updates.
          searchRunIdRef.current += 1;
          setIsSearching(false);
          setResults([]);
          setQuery("");
        }
      }}
    >
      <DialogContent
        showCloseButton={false}
        aria-describedby={undefined}
        className="flex h-160 max-w-180 flex-col gap-0 overflow-hidden p-0 sm:max-w-180"
      >
        <DialogTitle className="sr-only">Search Conversations</DialogTitle>
        {/* Header: search input (ChatGPT-style) */}
        <div className="flex w-full items-center gap-3 border-b border-line-2 px-4 py-3">
          <Search className="size-4 shrink-0 text-fg-3" aria-hidden />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search conversations..."
            className="h-8 w-full border-none bg-transparent text-md text-fg outline-hidden placeholder:text-fg-4"
          />
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label="Close search"
            onClick={() => onOpenChange(false)}
          >
            <X />
          </Button>
        </div>

        <div className="flex min-h-0 w-full flex-1 flex-col gap-2 px-3 pt-3 pb-3">
          <div className="flex h-6 w-full items-center justify-between px-2">
            <span className="text-sm text-fg-3">
              {normalizedQuery ? "Results" : "Recent Conversations"}
            </span>
            {isSearching ? (
              <div className="flex items-center gap-2">
                <Spinner className="size-3.5 text-fg-3" />
                <span className="text-sm text-fg-3">Searching…</span>
              </div>
            ) : null}
          </div>

          <div className="flex min-h-0 w-full flex-1 flex-col gap-0.5 overflow-auto">
            {displayResults.length === 0 ? (
              <EmptyState
                icon={Search}
                title="No Results"
                description="Try a different search term."
              />
            ) : (
              displayResults.map((r) => {
                const convoId = r.conversation.playground_conversation_id;
                const title = getConversationTitle(r.conversation);
                const ts = getConversationSubtitle(r.conversation);
                const selected = convoId === selectedConversationId;
                return (
                  <button
                    key={`${convoId}_${r.matchType}`}
                    type="button"
                    className={cn(
                      "flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-2 text-left transition-colors duration-100 hover:bg-fill-1 active:bg-fill-2",
                      selected && "bg-fill-2"
                    )}
                    onClick={() => {
                      onSelectConversation(convoId);
                      onOpenChange(false);
                    }}
                  >
                    <div className="flex w-full items-center justify-between gap-3">
                      <span className="truncate text-base font-medium text-fg">
                        {renderHighlightedText(title, normalizedQuery)}
                      </span>
                      {showRightTimestamp ? (
                        <span className="flex-none text-sm text-fg-3">
                          {ts}
                        </span>
                      ) : null}
                    </div>
                    <span className="text-sm text-fg-3">
                      {renderHighlightedText(r.subtitle, normalizedQuery)}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
